// Purpose: /shares/new — drag/drop, upload, finalize.
// Commit 8b.14: multi-recipient. Replaces the single recipient-email input
// with a chip-style multi-email entry; post-finalize displays per-recipient
// links + access codes in a one-time listing (after the user dismisses,
// codes require MFA-gated /recover-recipients to re-display).
// Commit 8b.15: folder uploads. Two collection paths: (a) flat files via
// drop or "Add files" → upload immediately (pre-8b.15 UX preserved); (b)
// folder via drop with directory entries or "Add folder" → preview-then-
// confirm UX with file count + size + folder names + hidden-file warning.

import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  completeFile,
  createDraft,
  finalize,
  presignFile,
  reportFileError,
  type CreatedRecipient,
  type FinalizeResponseMulti,
} from '../api/shares.ts';
import { PageHeader } from '../components/nav/PageHeader.tsx';
import { Button, Card, Input, ProgressFill, Select } from '../components/ui/index.ts';
import { uploadFile, type UploadProgress } from '../lib/upload.ts';
import {
  countHiddenFiles,
  flatFileList,
  hasFolderStructure,
  walkDataTransferItems,
  walkWebkitDirectoryInput,
  type FolderEntry,
} from '../lib/folder-walker.ts';

type FileState = {
  file: File;
  relativePath: string;
  fileId?: string;
  progress: number; // 0..1
  stage: UploadProgress['stage'] | 'queued' | 'error';
  error?: string;
};

const MAX_SIZE = 5 * 1024 * 1024 * 1024;
const MAX_RECIPIENTS = 25;
const MAX_FILES_PER_SHARE = 500;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function fmt(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function NewShare() {
  const navigate = useNavigate();
  const [draftId, setDraftId] = useState<string | null>(null);
  const [files, setFiles] = useState<FileState[]>([]);
  const [name, setName] = useState('');
  const [ttlHours, setTtlHours] = useState<1 | 2 | 8 | 24>(2);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [recipientInput, setRecipientInput] = useState('');
  const [recipientHint, setRecipientHint] = useState('');
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState<FinalizeResponseMulti | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  // Commit 8b.15: preview phase for folder drops. The user reviews the
  // file list (with directory structure + hidden-file warning) BEFORE any
  // bytes leave the device. Cancel discards; Submit kicks off the same
  // upload pipeline that flat-drop uses today.
  const [pendingFolder, setPendingFolder] = useState<FolderEntry[] | null>(null);

  const totalSize = useMemo(() => files.reduce((n, f) => n + f.file.size, 0), [files]);
  const allDone = files.length > 0 && files.every((f) => f.stage === 'done');

  async function ensureDraft(): Promise<string> {
    if (draftId) return draftId;
    const d = await createDraft();
    setDraftId(d.id);
    return d.id;
  }

  async function uploadOne(
    did: string,
    file: File,
    index: number,
    relativePath: string
  ): Promise<void> {
    try {
      await uploadFile(
        file,
        index,
        {
          presign: (input) => presignFile(did, input),
          complete: ({ fileId, rawDekBase64 }) => completeFile(did, fileId, rawDekBase64),
          reportError: ({ fileId, errorReason }) => reportFileError(did, fileId, errorReason),
        },
        (p) => {
          setFiles((prev) =>
            prev.map((fs, idx) =>
              idx === index
                ? {
                    ...fs,
                    progress: p.bytesEncrypted / Math.max(1, p.totalBytes),
                    stage: p.stage,
                  }
                : fs
            )
          );
        },
        relativePath || undefined
      );
    } catch (err) {
      const message = (err as Error).message;
      setFiles((prev) =>
        prev.map((fs, idx) => (idx === index ? { ...fs, stage: 'error', error: message } : fs))
      );
      throw err;
    }
  }

  // Commit 8b.15: shared upload path used by both immediate-flat-drop and
  // post-preview folder upload. Caller has already validated counts /
  // sizes / preview confirmation.
  async function uploadEntries(entries: FolderEntry[]): Promise<void> {
    if (entries.length === 0) return;
    const startIndex = files.length;
    const next: FileState[] = entries.map((e) => ({
      file: e.file,
      relativePath: e.relativePath,
      progress: 0,
      stage: 'queued',
    }));
    setFiles((prev) => [...prev, ...next]);

    let did: string;
    try {
      did = await ensureDraft();
    } catch (err) {
      setError((err as Error).message);
      return;
    }
    for (let i = 0; i < next.length; i++) {
      const index = startIndex + i;
      const entry = entries[i]!;
      try {
        await uploadOne(did, entry.file, index, entry.relativePath);
      } catch {
        // per-file error already recorded
      }
    }
  }

  // Validate count + total size against caps. Returns null on OK, message
  // on rejection. Used by both flat and folder paths.
  function checkCapsForBatch(incoming: FolderEntry[]): string | null {
    if (files.length + incoming.length > MAX_FILES_PER_SHARE) {
      return `Share file count would exceed ${MAX_FILES_PER_SHARE} (${files.length + incoming.length}).`;
    }
    const newSize =
      files.reduce((n, f) => n + f.file.size, 0) + incoming.reduce((n, e) => n + e.file.size, 0);
    if (newSize > MAX_SIZE) {
      return `Total size would exceed 5GB (${fmt(newSize)}).`;
    }
    return null;
  }

  // Commit 8b.15: flat-file drop / picker path — preserves pre-8b.15 UX
  // (immediate upload, no preview). Receives a FileList, filters noise,
  // hands straight to the upload pipeline.
  async function addFlatFiles(incoming: FileList | null) {
    if (!incoming || incoming.length === 0) return;
    const entries = flatFileList(incoming);
    if (entries.length === 0) return;
    const capError = checkCapsForBatch(entries);
    if (capError) {
      setError(capError);
      return;
    }
    setError(null);
    await uploadEntries(entries);
  }

  // Commit 8b.15: folder path — collects entries, runs cap checks, opens
  // the preview UI. The user confirms or cancels before any bytes leave.
  function stagePendingFolder(entries: FolderEntry[]) {
    if (entries.length === 0) return;
    const capError = checkCapsForBatch(entries);
    if (capError) {
      setError(capError);
      return;
    }
    setError(null);
    setPendingFolder(entries);
  }

  async function retryFile(index: number) {
    if (!draftId) return;
    const target = files[index];
    if (!target) return;
    setError(null);
    setFiles((prev) =>
      prev.map((fs, idx) => {
        if (idx !== index) return fs;
        const { error: _err, ...rest } = fs;
        void _err;
        return { ...rest, stage: 'queued', progress: 0 };
      })
    );
    try {
      await uploadOne(draftId, target.file, index, target.relativePath);
    } catch {
      // already recorded
    }
  }

  async function confirmPendingFolder() {
    const entries = pendingFolder;
    if (!entries) return;
    setPendingFolder(null);
    await uploadEntries(entries);
  }

  // Top-level drop handler. Walks DataTransferItemList; if any directory
  // entries surface, routes through the preview phase. Otherwise (flat
  // files only) routes through the immediate-upload path.
  async function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setError(null);
    let entries: FolderEntry[] = [];
    try {
      entries = await walkDataTransferItems(e.dataTransfer.items);
    } catch (err) {
      setError(`Could not read drop: ${(err as Error).message}`);
      return;
    }
    if (entries.length === 0) {
      // Fallback for browsers / events that don't expose webkitGetAsEntry —
      // use the legacy files property which is flat-only.
      const fl = e.dataTransfer.files;
      if (fl && fl.length > 0) await addFlatFiles(fl);
      return;
    }
    if (hasFolderStructure(entries)) {
      stagePendingFolder(entries);
    } else {
      // Pure flat drop — preserve pre-8b.15 UX.
      const capError = checkCapsForBatch(entries);
      if (capError) {
        setError(capError);
        return;
      }
      await uploadEntries(entries);
    }
  }

  // -------------------------------------------------------------------------
  // Recipient chip entry
  // -------------------------------------------------------------------------

  function addRecipientCandidates(raw: string) {
    // Split on commas, semicolons, whitespace — paste-comma-separated UX.
    const parts = raw
      .split(/[,;\s]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    setRecipients((prev) => {
      const seenLower = new Set(prev.map((p) => p.toLowerCase()));
      const out = [...prev];
      const skipped: string[] = [];
      for (const candidate of parts) {
        const lower = candidate.toLowerCase();
        if (seenLower.has(lower)) {
          skipped.push(candidate);
          continue;
        }
        if (!EMAIL_RE.test(candidate)) {
          skipped.push(candidate);
          continue;
        }
        if (out.length >= MAX_RECIPIENTS) break;
        out.push(candidate);
        seenLower.add(lower);
      }
      if (skipped.length > 0) {
        setError(`Skipped ${skipped.length} invalid/duplicate: ${skipped.slice(0, 3).join(', ')}`);
      } else {
        setError(null);
      }
      return out;
    });
    setRecipientInput('');
  }

  function removeRecipient(idx: number) {
    setRecipients((prev) => prev.filter((_, i) => i !== idx));
  }

  function onRecipientKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === 'Tab') {
      if (recipientInput.trim().length > 0) {
        e.preventDefault();
        addRecipientCandidates(recipientInput);
      }
    } else if (e.key === 'Backspace' && recipientInput.length === 0 && recipients.length > 0) {
      e.preventDefault();
      setRecipients((prev) => prev.slice(0, -1));
    }
  }

  const recipientsValid = recipients.length > 0 && recipients.length <= MAX_RECIPIENTS;
  const canFinalize = !!draftId && allDone && name.trim().length > 0 && recipientsValid;

  async function handleFinalize() {
    if (!draftId || !canFinalize) return;
    setCreating(true);
    setError(null);
    try {
      const res = await finalize(draftId, {
        name: name.trim(),
        ttlHours,
        recipients: recipients.map((email) => ({ email })),
        ...(recipientHint.trim() ? { recipientHint: recipientHint.trim() } : {}),
      });
      setResult(res);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  }

  // -------------------------------------------------------------------------
  // Post-finalize: one-time per-recipient code display
  // -------------------------------------------------------------------------

  if (result) {
    return (
      <div className="flex flex-col gap-6 max-w-3xl">
        <PageHeader
          title={`Share ready — ${result.recipients.length} recipient${result.recipients.length === 1 ? '' : 's'}`}
          subtitle="Each recipient has their own link and access code below. They received both via email already; this list is the only place you can re-copy them. After you leave this page, recovering codes requires MFA."
        />
        {result.recipients.map((r) => (
          <RecipientResultCard key={r.recipientId} recipient={r} />
        ))}
        <div>
          <Button variant="secondary" onClick={() => navigate('/shares')}>
            Done — back to shares
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <PageHeader title="New share" />

      <div
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDrop={(e) => void onDrop(e)}
        className="border-2 border-dashed border-border-default rounded-lg p-8 text-center bg-surface-elevated transition-colors duration-fast"
        data-testid="dropzone"
      >
        <p className="text-body-m text-fg-secondary">
          Drop files or a folder here. Max 5 GB total, {MAX_FILES_PER_SHARE} files.
        </p>
        <div className="mt-3 flex items-center justify-center gap-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => inputRef.current?.click()}
            data-testid="add-files"
          >
            Add files
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => folderInputRef.current?.click()}
            data-testid="add-folder"
          >
            Add folder
          </Button>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            void addFlatFiles(e.target.files);
            e.target.value = '';
          }}
        />
        {/* Commit 8b.15: webkitdirectory input. Each File carries
            webkitRelativePath; walker reads that into FolderEntry. The
            attribute is non-standard but supported in every desktop
            browser AST cares about (Chrome/Edge/Safari/Firefox). */}
        <input
          ref={folderInputRef}
          type="file"
          // @ts-expect-error webkitdirectory is non-standard but functional
          webkitdirectory=""
          directory=""
          multiple
          className="hidden"
          onChange={(e) => {
            const files = e.target.files;
            if (files && files.length > 0) {
              const entries = walkWebkitDirectoryInput(files);
              if (hasFolderStructure(entries)) {
                stagePendingFolder(entries);
              } else {
                // Edge case: webkitdirectory input with a single
                // file at the picked-folder root. Fall back to flat
                // upload via the regular path.
                const capError = checkCapsForBatch(entries);
                if (capError) {
                  setError(capError);
                } else {
                  void uploadEntries(entries);
                }
              }
            }
            e.target.value = '';
          }}
        />
      </div>

      {error ? (
        <div
          role="alert"
          className="px-4 py-3 rounded-md text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
        >
          {error}
        </div>
      ) : null}

      {pendingFolder ? (
        <FolderPreviewCard
          entries={pendingFolder}
          onCancel={() => setPendingFolder(null)}
          onConfirm={() => void confirmPendingFolder()}
        />
      ) : null}

      {files.length > 0 ? (
        <Card padding="none">
          <ul className="divide-y divide-border-subtle">
            {files.map((f, i) => (
              <li key={i} className="p-4">
                <div className="flex items-center justify-between mb-2 gap-4">
                  <div className="min-w-0 flex-1">
                    {f.relativePath ? (
                      <div className="text-caption text-fg-muted truncate">{f.relativePath}/</div>
                    ) : null}
                    <div className="text-body-m text-fg-primary truncate">{f.file.name}</div>
                  </div>
                  <div className="text-body-s text-fg-muted flex-shrink-0">{fmt(f.file.size)}</div>
                </div>
                <div className="h-2 bg-bg-tertiary rounded overflow-hidden">
                  <ProgressFill
                    value={f.progress * 100}
                    className={`h-full ${
                      f.stage === 'error' ? 'bg-status-danger' : 'bg-accent-primary'
                    }`}
                  />
                </div>
                <div className="text-body-s text-fg-muted mt-1 flex items-center justify-between gap-3">
                  <span className={f.stage === 'error' ? 'text-status-danger' : undefined}>
                    {f.stage === 'done'
                      ? 'uploaded'
                      : f.stage === 'error'
                        ? `error: ${f.error ?? 'unknown'}`
                        : f.stage}
                  </span>
                  {f.stage === 'error' ? (
                    <Button
                      variant="subtle"
                      size="sm"
                      onClick={() => void retryFile(i)}
                      data-testid={`retry-upload-${i}`}
                    >
                      Retry
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
            <li className="p-4 text-body-s text-fg-muted">
              Total {fmt(totalSize)} / {fmt(MAX_SIZE)}
            </li>
          </ul>
        </Card>
      ) : null}

      {allDone ? (
        <Card padding="comfortable">
          <div className="flex flex-col gap-5">
            <Input
              label="Share name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Offer letter draft"
              maxLength={200}
            />
            <Select
              label="Expires in"
              value={ttlHours}
              onChange={(e) => setTtlHours(Number(e.target.value) as 1 | 2 | 8 | 24)}
            >
              <option value={1}>1 hour</option>
              <option value={2}>2 hours</option>
              <option value={8}>8 hours</option>
              <option value={24}>24 hours</option>
            </Select>

            <div>
              <label className="block text-label uppercase text-fg-secondary mb-2">
                Recipients ({recipients.length}/{MAX_RECIPIENTS})
              </label>
              <div
                className="flex flex-wrap gap-2 p-2 bg-surface-elevated border border-border-default rounded-md min-h-[44px] cursor-text"
                onClick={() => {
                  const el = document.querySelector<HTMLInputElement>(
                    'input[data-testid="recipient-chip-input"]'
                  );
                  el?.focus();
                }}
              >
                {recipients.map((r, i) => (
                  <span
                    key={`${r}-${i}`}
                    className="inline-flex items-center gap-1 rounded-full bg-bg-tertiary px-3 py-1 text-body-s text-fg-primary"
                  >
                    {r}
                    <button
                      type="button"
                      aria-label={`Remove ${r}`}
                      className="text-fg-muted hover:text-status-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeRecipient(i);
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  data-testid="recipient-chip-input"
                  className="flex-1 min-w-[160px] bg-transparent outline-none text-body-m text-fg-primary placeholder:text-fg-muted"
                  type="email"
                  value={recipientInput}
                  onChange={(e) => setRecipientInput(e.target.value)}
                  onKeyDown={onRecipientKey}
                  onBlur={() => {
                    if (recipientInput.trim().length > 0) {
                      addRecipientCandidates(recipientInput);
                    }
                  }}
                  placeholder={
                    recipients.length === 0
                      ? 'recipient@example.com (Enter, comma, or paste a list)'
                      : ''
                  }
                  autoComplete="off"
                />
              </div>
              <p className="text-body-s text-fg-secondary mt-2">
                Each recipient gets a unique link and access code. They will be required to verify
                their email with a one-time code before they can download.
              </p>
            </div>

            <Input
              label="Recipient hint (optional)"
              type="text"
              value={recipientHint}
              onChange={(e) => setRecipientHint(e.target.value)}
              placeholder="Note to self about who this is for"
              maxLength={200}
            />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => navigate('/shares')}>
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={creating}
                disabled={!canFinalize}
                onClick={() => void handleFinalize()}
                data-testid="finalize-share"
              >
                Send to {recipients.length || 0} recipient{recipients.length === 1 ? '' : 's'}
              </Button>
            </div>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

// Commit 8b.15: folder-drop preview. Lists the unique top-level folders +
// total count + total size + hidden-file warning. User confirms or cancels
// before any bytes leave. The flat-drop path keeps its no-preview UX.
function FolderPreviewCard({
  entries,
  onCancel,
  onConfirm,
}: {
  entries: FolderEntry[];
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const totalBytes = entries.reduce((n, e) => n + e.file.size, 0);
  const topFolders = new Set(
    entries.map((e) => e.relativePath.split('/')[0] ?? '').filter(Boolean)
  );
  const hiddenCount = countHiddenFiles(entries);
  const sample = entries.slice(0, 8);
  return (
    <Card padding="comfortable">
      <div className="flex flex-col gap-3">
        <div className="text-label uppercase text-fg-secondary">Folder upload preview</div>
        <div className="text-body-m text-fg-primary">
          {entries.length} file{entries.length === 1 ? '' : 's'}, {fmt(totalBytes)}
          {topFolders.size > 0
            ? ` across ${topFolders.size} folder${topFolders.size === 1 ? '' : 's'} (${[...topFolders].slice(0, 3).join(', ')}${topFolders.size > 3 ? '…' : ''})`
            : ''}
        </div>
        {hiddenCount > 0 ? (
          <div className="text-body-s text-status-warning">
            {hiddenCount} hidden file{hiddenCount === 1 ? '' : 's'} (leading dot) included. macOS
            noise (`.DS_Store`, `._*`) and Windows noise (`Thumbs.db`, `desktop.ini`) is already
            filtered.
          </div>
        ) : null}
        <ul className="text-body-s text-fg-muted divide-y divide-border-subtle">
          {sample.map((e, i) => (
            <li key={i} className="py-1 truncate">
              {e.relativePath ? `${e.relativePath}/` : ''}
              <span className="text-fg-primary">{e.file.name}</span>
            </li>
          ))}
          {entries.length > sample.length ? (
            <li className="py-1">…and {entries.length - sample.length} more.</li>
          ) : null}
        </ul>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={onConfirm} data-testid="confirm-folder">
            Upload {entries.length} file{entries.length === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function RecipientResultCard({ recipient }: { recipient: CreatedRecipient }) {
  return (
    <Card padding="compact">
      <div className="flex flex-col gap-3">
        <div className="text-label uppercase text-fg-secondary">{recipient.email}</div>
        <div>
          <div className="text-caption uppercase text-fg-muted mb-1">Link</div>
          <div className="flex items-center gap-2">
            <code className="text-body-s break-all text-fg-primary flex-1">{recipient.link}</code>
            <Button
              variant="subtle"
              size="sm"
              onClick={() => void navigator.clipboard.writeText(recipient.link)}
            >
              Copy
            </Button>
          </div>
        </div>
        <div>
          <div className="text-caption uppercase text-fg-muted mb-1">Access code</div>
          <div className="flex items-center gap-2">
            <code className="text-heading-m font-mono tracking-widest text-fg-primary">
              {recipient.accessCode}
            </code>
            <Button
              variant="subtle"
              size="sm"
              onClick={() => void navigator.clipboard.writeText(recipient.accessCode)}
            >
              Copy
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
